export interface ProviderConfig {
  id: string
  name: string
  docsUrl: string
  keyPlaceholder: string
  comingSoon?: boolean
}

export const PROVIDERS: ProviderConfig[] = [
  {
    id: 'replicate',
    name: 'Replicate',
    docsUrl: 'https://replicate.com/account/api-tokens',
    keyPlaceholder: 'r8_...',
  },
  {
    id: 'google-gemini',
    name: 'Google Gemini / Imagen',
    docsUrl: 'https://aistudio.google.com/app/apikey',
    keyPlaceholder: 'AIzaSy...',
  },
  {
    id: 'fal',
    name: 'Fal.ai',
    docsUrl: 'https://fal.ai/dashboard/keys',
    keyPlaceholder: 'fal_...',
    comingSoon: true,
  },
  {
    id: 'cloudflare-ai',
    name: 'Cloudflare Workers AI',
    docsUrl: 'https://developers.cloudflare.com/ai-gateway/get-started/',
    keyPlaceholder: 'ACCOUNT_ID:GATEWAY_SLUG:API_TOKEN',
  },
]

export function getProvider(id: string): ProviderConfig | undefined {
  return PROVIDERS.find((p) => p.id === id)
}

export type TestKeyResult =
  | { status: 'valid' }
  | { status: 'invalid'; message: string }
  | { status: 'network-error'; message: string }
  | { status: 'rate-limited' }

export type TestState =
  | { phase: 'idle' }
  | { phase: 'testing' }
  | { phase: 'done'; result: TestKeyResult }

export async function testProviderKey(provider: string, rawKey: string): Promise<TestKeyResult> {
  try {
    if (provider === 'replicate') {
      return await testReplicate(rawKey)
    }
    if (provider === 'google-gemini') {
      return await testGoogleGemini(rawKey)
    }
    if (provider === 'cloudflare-ai') {
      return testCloudflareAi(rawKey)
    }
    return { status: 'invalid', message: 'Provider does not support key testing yet.' }
  } catch {
    return { status: 'network-error', message: 'Could not verify — network error' }
  }
}

async function testReplicate(apiKey: string): Promise<TestKeyResult> {
  const res = await fetch('https://api.replicate.com/v1/account', {
    headers: { Authorization: `Bearer ${apiKey}` },
  })
  if (res.status === 401) return { status: 'invalid', message: 'Invalid API key' }
  if (res.status === 429) return { status: 'rate-limited' }
  if (!res.ok) return { status: 'network-error', message: 'Could not verify — provider error' }
  return { status: 'valid' }
}

function testCloudflareAi(apiKey: string): TestKeyResult {
  // AI Gateway supports browser CORS but any validation call triggers inference (costs money).
  // Validate format only: must be ACCOUNT_ID:GATEWAY_SLUG:API_TOKEN with all three parts non-empty.
  const err = getCloudflareKeyFormatError(apiKey)
  if (err) return { status: 'invalid', message: err }
  return { status: 'valid' }
}

export function getCloudflareKeyFormatError(apiKey: string): string | null {
  const first = apiKey.indexOf(':')
  const second = apiKey.indexOf(':', first + 1)
  if (
    first === -1 ||
    second === -1 ||
    first === 0 ||
    second === first + 1 ||
    second === apiKey.length - 1
  )
    return 'Key must be in the format ACCOUNT_ID:GATEWAY_SLUG:API_TOKEN'
  const accountId = apiKey.slice(0, first)
  const gatewaySlug = apiKey.slice(first + 1, second)
  if (!/^[a-zA-Z0-9_-]+$/.test(accountId) || !/^[a-zA-Z0-9_-]+$/.test(gatewaySlug))
    return 'Account ID and Gateway Slug must be alphanumeric (dashes/underscores allowed)'
  return null
}

async function testGoogleGemini(apiKey: string): Promise<TestKeyResult> {
  // Use x-goog-api-key header — never pass keys as URL query params
  const res = await fetch('https://generativelanguage.googleapis.com/v1beta/models', {
    headers: { 'x-goog-api-key': apiKey },
  })
  if (res.status === 400 || res.status === 403)
    return { status: 'invalid', message: 'Invalid API key' }
  if (res.status === 429) return { status: 'rate-limited' }
  if (!res.ok) return { status: 'network-error', message: 'Could not verify — provider error' }
  return { status: 'valid' }
}
